// A-14 — Modale de conversion ticket → dossier.
// Pré-remplit l'intitulé depuis le ticket (sender_name + extrait du message).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Briefcase, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  type Dossier,
  type DossierType,
  DOSSIER_TYPE_LABELS,
} from '../../services/dossiers/dossierService';
import { dossiersKeys } from '../../hooks/queries/useDossiers';
import { applyFolderTemplate } from '../../services/files/filesService';
import { shouldApplyFolderTemplate, type FolderTemplateChoice } from '../../lib/folderTemplates';
import { FolderTemplatePicker } from '../files/FolderTemplatePicker';

interface Props {
  ticketId: string;
  defaultTitle: string;       // pré-rempli depuis le sujet du ticket
  defaultClientName?: string; // sender_name si dispo
  onClose: () => void;
}

const TYPES: DossierType[] = [
  'civil', 'penal', 'commercial', 'social',
  'immobilier', 'famille', 'admin', 'autre',
];

export function TicketConvertModal({
  ticketId, defaultTitle, defaultClientName, onClose,
}: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [title, setTitle] = useState(defaultTitle);
  const [type, setType] = useState<DossierType>('autre');
  const [folderTemplate, setFolderTemplate] = useState<FolderTemplateChoice>('none');

  const convert = useMutation({
    mutationFn: async (): Promise<Dossier> => {
      const { data, error } = await supabase.rpc('convert_ticket_to_dossier', {
        p_ticket_id: ticketId,
        p_title: title.trim(),
        p_type_affaire: type,
      });
      if (error) throw error;
      const dossier = data as Dossier;
      if (shouldApplyFolderTemplate(folderTemplate, 0) && folderTemplate !== 'none') {
        try {
          await applyFolderTemplate(dossier.id, folderTemplate);
        } catch {
          // Dossier déjà créé — l’arborescence se pose ensuite depuis Documents.
        }
      }
      return dossier;
    },
    onSuccess: (dossier) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/dossiers/${dossier.id}`);
      onClose();
    },
  });

  const canSubmit = title.trim().length > 0 && !convert.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-cime-primary" />
            <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
              Convertir en dossier
            </h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 py-5 space-y-4">
          {defaultClientName && (
            <div className="text-xs text-cime-text-muted bg-neutral-50 border border-neutral-200 rounded-md p-2.5">
              Client : <strong className="text-cime-text-primary">{defaultClientName}</strong>
              <p className="mt-0.5 italic">
                Le contenu du ticket sera repris dans la description du dossier.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
              Type d'affaire
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn(
                    'px-2 py-2 text-xs rounded-md border transition',
                    type === t
                      ? 'bg-cime-primary text-white border-cime-primary'
                      : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                  )}
                >
                  {DOSSIER_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
              Intitulé du dossier <span className="text-red-500">*</span>
            </label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Dupont c/ Martin — Litige…"
            />
            <p className="mt-1 text-[11px] text-cime-text-muted">
              Le numéro CIME-2026-XXXX sera attribué automatiquement et le ticket
              passera en statut « converti ».
            </p>
          </div>

          <FolderTemplatePicker value={folderTemplate} onChange={setFolderTemplate} />

          {convert.isError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
              {convert.error instanceof Error ? convert.error.message : 'Erreur à la conversion.'}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" onClick={onClose} disabled={convert.isPending}>Annuler</Button>
          <Button onClick={() => convert.mutate()} disabled={!canSubmit}>
            {convert.isPending ? 'Conversion…' : 'Convertir en dossier'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
